// Written by Claude Code on 2026-08-09
// User prompt: Magic Patterns redesign of the sign-in page (CenteredSignIn), using the
// existing app logo in place of the placeholder mark, with a real forgot-password flow.

import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  ArrowLeftIcon,
  ShieldCheckIcon,
  CheckCircleIcon,
  AlertCircleIcon,
  EyeIcon,
  EyeOffIcon,
  Loader2Icon,
} from 'lucide-react';
import {
  signInWithEmailAndPassword,
  sendPasswordResetEmail,
  onAuthStateChanged,
} from 'firebase/auth';
import { auth } from '../../firebase';

function Login() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isWelcome = searchParams.get('welcome') === '1';
  const redirectTo = searchParams.get('redirect');

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [resetStatus, setResetStatus] = useState('idle'); // idle | sending | sent

  // If this tab already has an active session, skip the login page
  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (user) => {
      if (user) navigate(getDestination(redirectTo), { replace: true });
    });
    return unsub;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await signInWithEmailAndPassword(auth, email, password);
      navigate(getDestination(redirectTo), { replace: true });
    } catch (err) {
      setError(getErrorMessage(err.code));
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    if (!email || !email.includes('@')) {
      setError('Enter your email above first, then click "Forgot password?"');
      return;
    }
    setError('');
    setResetStatus('sending');
    try {
      await sendPasswordResetEmail(auth, email);
    } catch {
      // Same outcome whether or not the account exists — avoids revealing which
      // emails are registered.
    } finally {
      setResetStatus('sent');
    }
  };

  const fieldClass =
    'w-full rounded-lg border border-ink-200 bg-white px-3.5 py-2.5 text-[15px] text-ink-900 placeholder:text-ink-400 transition-colors focus:border-brand-600 focus:outline-none focus:ring-4 focus:ring-brand-600/15';

  return (
    <main className="bg-grid flex min-h-screen w-full flex-col bg-ink-50">
      <header className="px-6 py-5">
        <button
          onClick={() => navigate('/')}
          className="inline-flex items-center gap-2 rounded text-sm font-medium text-ink-500 transition-colors hover:text-ink-800 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-600/40"
        >
          <ArrowLeftIcon className="h-4 w-4" aria-hidden="true" />
          Back
        </button>
      </header>

      <div className="flex flex-1 items-center justify-center px-6 pb-16">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          className="w-full max-w-[420px]"
        >
          <div className="flex flex-col items-center text-center">
            <div className="rounded-2xl border border-ink-200 bg-white p-3.5 shadow-sm">
              <img src="/logo-icon.png" alt="Certify Technology" className="h-11 w-11 object-contain" />
            </div>
            <div className="mt-4 text-[13px] font-bold tracking-[0.22em] text-ink-900">CERTIFY</div>
            <div className="mt-1 text-[10px] font-semibold tracking-[0.28em] text-brand-600">
              TECHNOLOGY
            </div>
          </div>

          <div className="mt-8">
            <h1 className="text-xl font-semibold tracking-tight text-ink-900">Sign In</h1>
            <p className="mt-1.5 text-sm text-ink-500">
              FMLA &amp; leave management for your organization.
            </p>

            {isWelcome && (
              <div className="mt-6 flex items-start gap-3 px-4 py-3 rounded-xl bg-emerald-50 ring-1 ring-emerald-200 text-sm text-emerald-800">
                <CheckCircleIcon className="w-4 h-4 flex-shrink-0 mt-0.5 text-emerald-600" />
                <div>
                  <p className="font-semibold">Password set — you're all set!</p>
                  <p className="text-emerald-700 text-xs mt-0.5">Sign in below to access your account and complete onboarding.</p>
                </div>
              </div>
            )}

            <form onSubmit={handleSubmit} className="mt-6 space-y-5" noValidate>
              {error && (
                <div
                  role="alert"
                  className="flex items-start gap-2.5 rounded-lg border border-red-200 bg-red-50 px-3.5 py-3 text-sm text-red-800"
                >
                  <AlertCircleIcon className="mt-0.5 h-4 w-4 flex-none" aria-hidden="true" />
                  <span>{error}</span>
                </div>
              )}

              <div className="space-y-1.5">
                <label htmlFor="email" className="block text-sm font-medium text-ink-700">
                  Email
                </label>
                <input
                  id="email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@company.com"
                  className={fieldClass}
                />
              </div>

              <div className="space-y-1.5">
                <div className="flex items-baseline justify-between">
                  <label htmlFor="password" className="block text-sm font-medium text-ink-700">
                    Password
                  </label>
                  {resetStatus === 'sent' ? (
                    <span className="text-sm text-brand-700">Check your email for a reset link</span>
                  ) : (
                    <button
                      type="button"
                      onClick={handleForgotPassword}
                      disabled={resetStatus === 'sending'}
                      className="rounded text-sm font-medium text-brand-700 underline-offset-4 hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-600/40 disabled:opacity-60"
                    >
                      {resetStatus === 'sending' ? 'Sending…' : 'Forgot password?'}
                    </button>
                  )}
                </div>
                <div className="relative">
                  <input
                    id="password"
                    name="password"
                    type={showPassword ? 'text' : 'password'}
                    autoComplete="current-password"
                    required
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="••••••••"
                    className={`${fieldClass} pr-11`}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((v) => !v)}
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                    className="absolute inset-y-0 right-0 flex w-11 items-center justify-center rounded-r-lg text-ink-400 transition-colors hover:text-ink-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-600/40"
                  >
                    {showPassword ? (
                      <EyeOffIcon className="h-4 w-4" aria-hidden="true" />
                    ) : (
                      <EyeIcon className="h-4 w-4" aria-hidden="true" />
                    )}
                  </button>
                </div>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="flex w-full items-center justify-center gap-2 rounded-lg bg-brand-600 px-4 py-3 text-[15px] font-semibold text-white transition-colors hover:bg-brand-700 focus:outline-none focus-visible:ring-4 focus-visible:ring-brand-600/30 disabled:cursor-not-allowed disabled:opacity-70"
              >
                {loading && <Loader2Icon className="h-4 w-4 animate-spin" aria-hidden="true" />}
                {loading ? 'Signing in…' : 'Sign in'}
              </button>
            </form>
          </div>

          <div className="mt-6 flex items-start justify-center gap-2 px-4 text-center text-[13px] leading-relaxed text-ink-500">
            <ShieldCheckIcon className="mt-0.5 h-4 w-4 flex-none text-ink-400" aria-hidden="true" />
            <p>Access is invite-only. Contact your administrator if you need an account.</p>
          </div>
        </motion.div>
      </div>
    </main>
  );
}

function getDestination(redirect) {
  if (redirect && redirect.startsWith('/app/')) return redirect;
  return '/app/dashboard';
}

function getErrorMessage(code) {
  switch (code) {
    case 'auth/invalid-email':
      return 'Invalid email address.';
    case 'auth/user-not-found':
    case 'auth/wrong-password':
    case 'auth/invalid-credential':
      return 'Incorrect email or password.';
    case 'auth/too-many-requests':
      return 'Too many attempts. Please try again later.';
    default:
      return 'Something went wrong. Please try again.';
  }
}

export default Login;
